/**
 * Keys + defaults for admin-editable marketing content, grouped by
 * subpage. Each entry under SECTIONS becomes a separate admin route.
 * HomePage imports `pick`; the admin editor imports `SECTIONS`.
 */

export const SECTIONS = {
  homepage: {
    title:       "Homepage",
    eyebrow:     "HOMEPAGE",
    description: "Hero section and the top admission announcement that visitors see first on ncla.edu.",
    groups: [
      { heading: "Hero section", items: [
        { key: "hero.eyebrow", label: "Eyebrow",  multiline: false },
        { key: "hero.title",   label: "Headline", multiline: true, rows: 2, hint: "Use a line break for two-line headlines." },
        { key: "hero.sub",     label: "Subtitle", multiline: true, rows: 3 },
        { key: "hero.phone",   label: "Phone number shown in hero" },
      ]},
      { heading: "Admission announcement banner", items: [
        { key: "admission.enabled", label: "Show banner? (yes/no)", hint: "Type 'yes' to display the red banner at the very top of the public homepage." },
        { key: "admission.text",    label: "Banner message", multiline: true, rows: 2 },
      ]},
    ],
  },

  about: {
    title:       "About Us",
    eyebrow:     "ABOUT US",
    description: "About paragraph and the Mission / Vision cards that appear below the hero.",
    groups: [
      { heading: "About section", items: [
        { key: "about.title", label: "Section label" },
        { key: "about.body",  label: "About paragraph", multiline: true, rich: true, rows: 6 },
      ]},
      { heading: "Mission & Vision", items: [
        { key: "mission.title", label: "Mission title" },
        { key: "mission.body",  label: "Mission statement", multiline: true, rich: true, rows: 4 },
        { key: "vision.title",  label: "Vision title" },
        { key: "vision.body",   label: "Vision statement",  multiline: true, rich: true, rows: 4 },
      ]},
    ],
  },

  programs: {
    title:       "Programs",
    eyebrow:     "PROGRAMS",
    description: "Each program has a short card (shown on the homepage) and a full detail page at /programs/<slug>. Keep the title short; taglines are clamped to 3 lines on the card; the body appears on the detail page.",
    groups: [
      { heading: "Section heading", items: [
        { key: "programs.heading", label: "Section title" },
      ]},
      { heading: "Pre-school", items: [
        { key: "programs.preschool",         label: "Card title",  hint: "Keep short — e.g. Pre-School" },
        { key: "programs.preschool.tagline", label: "Card tagline", multiline: true, rows: 3, hint: "1–3 sentence teaser shown on the card. Clamped to 3 lines." },
        { key: "programs.preschool.body",    label: "Full description (detail page)", multiline: true, rich: true, rows: 8},
      ]},
      { heading: "Grade School", items: [
        { key: "programs.grade",         label: "Card title" },
        { key: "programs.grade.tagline", label: "Card tagline", multiline: true, rows: 3 },
        { key: "programs.grade.body",    label: "Full description (detail page)", multiline: true, rich: true, rows: 8},
      ]},
      { heading: "Junior High", items: [
        { key: "programs.junior",         label: "Card title" },
        { key: "programs.junior.tagline", label: "Card tagline", multiline: true, rows: 3 },
        { key: "programs.junior.body",    label: "Full description (detail page)", multiline: true, rich: true, rows: 8},
      ]},
      { heading: "Senior High", items: [
        { key: "programs.senior",         label: "Card title" },
        { key: "programs.senior.tagline", label: "Card tagline", multiline: true, rows: 3 },
        { key: "programs.senior.body",    label: "Full description (detail page)", multiline: true, rich: true, rows: 8},
      ]},
    ],
  },

  contact: {
    title:       "Contact Info",
    eyebrow:     "CONTACT",
    description: "Phone, email, and address shown in the Contact section at the bottom of the homepage.",
    groups: [
      { heading: "Contact details", items: [
        { key: "contact.phone",   label: "Phone" },
        { key: "contact.email",   label: "Email" },
        { key: "contact.address", label: "Address", multiline: true, rows: 2 },
      ]},
    ],
  },

  footer: {
    title:       "Footer",
    eyebrow:     "FOOTER",
    description: "Footer description paragraph and copyright line.",
    groups: [
      { heading: "Footer text", items: [
        { key: "footer.description", label: "Description", multiline: true, rows: 3 },
        { key: "footer.copyright",   label: "Copyright line" },
      ]},
    ],
  },
};

export const DEFAULTS = {
  "hero.eyebrow": "Manage your school",
  "hero.title":   "Learn just about anywhere.\nGrow faster.",
  "hero.sub":     "Online or in the classroom, we've built the tools your school needs to manage students, programs, and progress—securely.",
  "hero.phone":   "+65 6510 4541",

  "about.title":  "About Us",
  "about.body":   "Northpoint Center for Learning and Advancement (NCLA) is a community of educators, families, and staff dedicated to helping every student learn, grow, and thrive.",

  "mission.title": "Our Mission",
  "mission.body":  "To deliver a modern, values-driven education that prepares students for a changing world.",
  "vision.title":  "Our Vision",
  "vision.body":   "A learning community where every child is known, challenged, and inspired.",

  "programs.heading":   "Explore NCLA Programs",

  "programs.preschool":         "Pre-School",
  "programs.preschool.tagline": "A fun and nurturing environment where young learners develop social skills, creativity, and early learning foundations.",
  "programs.preschool.body":    "",

  "programs.grade":         "Grade School",
  "programs.grade.tagline": "Building strong academic foundations in reading, math, and critical thinking while encouraging curiosity and confidence.",
  "programs.grade.body":    "",

  "programs.junior":         "Junior High",
  "programs.junior.tagline": "Preparing students for higher education through structured learning, discipline, and skill development.",
  "programs.junior.body":    "",

  "programs.senior":         "Senior High",
  "programs.senior.tagline": "Focused academic tracks and real-world preparation to equip students for college and future careers.",
  "programs.senior.body":    "",

  "admission.enabled": "no",
  "admission.text":    "Admissions for the coming school year are now open. Visit us or call to schedule a tour.",

  "contact.phone":   "+65 6510 4541",
  "contact.email":   "hello@ncla.edu",
  "contact.address": "Northpoint Center for Learning and Advancement",

  "footer.description": "Empowering schools, educators, and coaches with clarity and modern design.",
  "footer.copyright":   "© 2026 Northpoint Center for Learning and Advancement (NCLA). All rights reserved.",
};

/** Reads a value for a key, falling back to the default. */
export function pick(map, key) {
  if (!map) return DEFAULTS[key] ?? "";
  const v = map[key];
  return (v === undefined || v === null || v === "") ? (DEFAULTS[key] ?? "") : v;
}

/** All keys that belong to a given section. Used for partial save payloads. */
export function keysInSection(sectionKey) {
  const section = SECTIONS[sectionKey];
  if (!section) return [];
  const keys = [];
  for (const group of section.groups) {
    for (const f of group.items) keys.push(f.key);
  }
  return keys;
}
